import React, { useState, useEffect } from 'react';
import {
  Home as HomeIcon, Heart, MapPin, Star, ArrowRight, Phone, Mail, Facebook, Twitter, Instagram, Send
} from 'react-feather';
import coupleImage from '/images/couple.jpg';
import welcomeVideo from '/images/video.mp4';

const testimonials = [
  {
    name: "Rajesh Patel",
    location: "New Jersey, USA",
    feedback:
      "NRI Local Buddy has been a true blessing! I finally have peace of mind knowing my parents in Ahmedabad are being cared for with love and professionalism.",
    image: "https://randomuser.me/api/portraits/men/45.jpg",
  },
  {
    name: "Priya Menon",
    location: "Melbourne, Australia",
    feedback:
      "Their regular updates and personal touch make me feel like I’m right there with my family. Truly a compassionate and reliable service!",
    image: "https://randomuser.me/api/portraits/women/47.jpg",
  },
  {
    name: "Arun Kumar",
    location: "Dubai, UAE",
    feedback:
      "What stands out is the empathy. They treat our elders like their own. Professional, transparent, and deeply caring!",
    image: "https://randomuser.me/api/portraits/men/52.jpg",
  },
];

const slidesData = [
  {
    title: "Celebrate With Your Loved Ones",
    image: "https://images.unsplash.com/photo-1513151233558-d860c5398176?q=80&w=2070&auto=format&fit=crop",
    subtitle: "Celebrate birthdays, anniversaries, and festivals with your loved ones — no matter the distance.",
    cta: "Plan a Celebration",
    link: "/celebrate",
  },
  {
    title: "Trusted Financial Advisory",
    subtitle: "Unbiased, tax-efficient financial advice to secure your family's future.",
    image: "https://images.unsplash.com/photo-1554224155-1696413565d3?q=80&w=2070&auto=format&fit=crop",
    cta: "Get Financial Advice",
    link: "/financial-advisory",
  },
  {
    title: "Compassionate Parental Care",
    subtitle: "Thoughtful care, trusted assistance, and peace of mind for your loved ones back home.",
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=2070&auto=format&fit=crop",
    cta: "Arrange Care for Parents",
    link: "/parental-care",
  },
  {
    title: "Secure Property Monitoring",
    subtitle: "Keep your property safe and well-maintained with remote monitoring.",
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=1973&auto=format&fit=crop",
    cta: "Secure Your Property",
    link: "/property-care",
  }
];

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slidesData.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <main className="bg-white text-gray-800">
      {/* HERO SECTION (Full Width) */}
      <section className="relative min-h-[480px] h-[85vh] flex items-center justify-center overflow-hidden text-center">
        {slidesData.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-[1500ms] ${index === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
              }`}
            style={{
              backgroundImage: `url('${slide.image}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#0F2D52]/90 via-[#0F2D52]/60 to-black/40"></div>
          </div>
        ))}

        <div className="relative z-20 px-4 sm:px-6 max-w-3xl mx-auto text-white">
          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 drop-shadow-lg">
            {slidesData[currentSlide].title}
          </h1>
          <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-90 leading-relaxed">
            {slidesData[currentSlide].subtitle}
          </p>
          <a
            href={slidesData[currentSlide].link}
            className="inline-flex items-center px-6 sm:px-8 py-3.5 sm:py-4 bg-[#C89B3C] rounded-full text-white font-semibold hover:bg-[#ab812f] transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base"
          >
            {slidesData[currentSlide].cta}
            <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
          </a>
        </div>

        <div className="absolute bottom-6 sm:bottom-10 flex space-x-3 justify-center w-full z-30">
          {slidesData.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${index === currentSlide ? "bg-white w-6" : "bg-white/50"
                }`}
            />
          ))}
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section
        id="about"
        className="py-12 sm:py-20 lg:py-28 container mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-8 lg:gap-16 items-center"
      >
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-[#0F2D52]">Your Bridge to Home</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#0F2D52] to-[#C89B3C] mb-6 sm:mb-10"></div>
          <p className="text-gray-600 text-base sm:text-lg mb-6 leading-relaxed">
            Living abroad offers opportunities, yet the longing for home remains. We help you care for your loved ones while you focus on your goals.
          </p>
          <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
            <span className="font-bold text-[#0F2D52]">NRI Local Buddy</span> bridges the miles between you and your family, ensuring their well-being and happiness.
          </p>
        </div>
        <img
          src={coupleImage}
          alt="Elderly couple smiling"
          className="rounded-2xl shadow-xl hover:scale-[1.03] transition-transform duration-300 w-full object-cover"
        />
      </section>


      {/* SERVICES SECTION */}
      <section id="services" className="py-12 sm:py-20 lg:py-28 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F2D52] mb-10 sm:mb-16">Comprehensive Care Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-10">
            {[
              { icon: <HomeIcon />, title: "Property Care", border: "#0F2D52" },
              { icon: <Heart />, title: "Parental Care", border: "#0E8A6A" },
              { icon: <MapPin />, title: "Financial Advisory", border: "#C89B3C" },
              { icon: <Star />, title: "Celebration", border: "#C89B3C" },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 sm:p-8 lg:p-10 shadow-lg border-t-4 transition-all hover:shadow-2xl hover:-translate-y-2"
                style={{ borderColor: item.border }}
              >
                <div
                  className="w-14 h-14 sm:w-16 sm:h-16 mx-auto flex items-center justify-center text-white rounded-2xl mb-6"
                  style={{ backgroundColor: item.border }}
                >
                  {item.icon}
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                  Reliable services ensuring peace of mind while you’re abroad.
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO SECTION */}
      <section className="relative min-h-[420px] py-16 lg:h-[650px] flex items-center justify-center overflow-hidden">
        <video
          src={welcomeVideo}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        ></video>
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 text-center text-white px-4 sm:px-8 md:px-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-8">Experience the Heart of Home</h2>
          <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-10 max-w-3xl mx-auto opacity-90 leading-relaxed">
            Watch how NRI Local Buddy connects families, celebrates togetherness, and builds trust that transcends distance.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 sm:px-10 py-4 sm:py-5 bg-[#C89B3C] rounded-full text-white font-semibold hover:bg-[#ab812f] transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base"
          >
            Get in Touch
            <ArrowRight className="ml-2 w-5 h-5" />
          </a>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section
        id="testimonials"
        className="py-12 sm:py-20 lg:py-28 bg-gradient-to-br from-[#0F2D52] to-[#081a30] text-white"
      >
        <div className="container mx-auto px-4 sm:px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-10 sm:mb-16">What Our Clients Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
            {[...testimonials].map((t, i) => (
              <div
                key={i}
                className="bg-white/10 rounded-2xl p-6 sm:p-8 lg:p-10 border border-white/20 hover:bg-white/15 transition-all"
              >
                <div className="flex justify-center mb-6">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} fill="currentColor" className="text-yellow-400 w-5 h-5" />
                  ))}
                </div>
                <p className="italic text-gray-200 mb-6 leading-relaxed text-sm sm:text-base">{t.feedback}</p>
                <p className="font-semibold text-white">{t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section className="py-12 sm:py-20 container mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        {/* Contact Info */}
        <div className="space-y-6 sm:space-y-10">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0F2D52] mb-3">Our Office</h2>
            <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
              NRI Local Buddy is headquartered in Hyderabad, India. Reach out to us for any inquiries or service requests. We ensure a prompt response.
            </p>
          </div>

          <div className="space-y-4 sm:space-y-6 text-sm sm:text-base">
            <div className="flex items-center space-x-4">
              <Phone className="text-[#C89B3C] flex-shrink-0" />
              <a href="https://wa.me/919618222498" className="hover:text-[#ab812f] transition">
                +91 9618222498
              </a>
            </div>
            <div className="flex items-center space-x-4">
              <Mail className="text-[#C89B3C] flex-shrink-0" />
              <a href="mailto:hello@ay-technologies.com" className="hover:text-[#ab812f] transition break-all">
                hello@ay-technologies.com
              </a>
            </div>
            <div className="flex items-start space-x-4">
              <MapPin className="text-[#C89B3C] mt-1 flex-shrink-0" />
              <p>112, Telkar Chambers, Tara Nagar, Chanda Nagar, Hyderabad, Telangana 500019, India</p>
            </div>
          </div>

          <div className="flex space-x-6 pt-4 sm:pt-6">
            <a href="#" className="text-[#0F2D52] hover:text-[#C89B3C]">
              <Facebook />
            </a>
            <a href="#" className="text-[#0F2D52] hover:text-[#C89B3C]">
              <Twitter />
            </a>
            <a href="#" className="text-[#0F2D52] hover:text-[#C89B3C]">
              <Instagram />
            </a>
          </div>
        </div>

        {/* Contact Form */}
        <form className="bg-gray-50 p-6 sm:p-8 lg:p-10 rounded-2xl shadow-md space-y-4 sm:space-y-6">
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#0F2D52] mb-2 sm:mb-4">Send Us a Message</h2>

          <div>
            <label className="block font-semibold mb-2 text-sm sm:text-base">Full Name</label>
            <input
              type="text"
              placeholder="Enter your full name"
              className="w-full border border-gray-300 rounded-xl px-4 sm:px-5 py-3 sm:py-4 focus:ring-2 focus:ring-[#0F2D52] outline-none text-sm sm:text-base"
            />
          </div>

          <div>
            <label className="block font-semibold mb-2 text-sm sm:text-base">Email Address</label>
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full border border-gray-300 rounded-xl px-4 sm:px-5 py-3 sm:py-4 focus:ring-2 focus:ring-[#0F2D52] outline-none text-sm sm:text-base"
            />
          </div>

          <div>
            <label className="block font-semibold mb-2 text-sm sm:text-base">Subject</label>
            <input
              type="text"
              placeholder="Subject of your message"
              className="w-full border border-gray-300 rounded-xl px-4 sm:px-5 py-3 sm:py-4 focus:ring-2 focus:ring-[#0F2D52] outline-none text-sm sm:text-base"
            />
          </div>

          <div>
            <label className="block font-semibold mb-2 text-sm sm:text-base">Message</label>
            <textarea
              rows="5"
              placeholder="Write your message here..."
              className="w-full border border-gray-300 rounded-xl px-4 sm:px-5 py-3 sm:py-4 focus:ring-2 focus:ring-[#0F2D52] outline-none text-sm sm:text-base"
            ></textarea>
          </div>

          <button
            type="submit"
            className="w-full bg-[#C89B3C] text-white font-semibold py-3.5 sm:py-4 rounded-xl hover:bg-[#ab812f] flex items-center justify-center gap-2 transition-all duration-300 text-sm sm:text-base"
          >
            <Send className="w-5 h-5" /> Send Message
          </button>
        </form>
      </section>

    </main>
  );
};

export default Home;
